import {
  ReactElement, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { colors } from 'styles/colors';
import { getUserProfile } from '~/helpers/userProfile';
import { useHealthDataContext } from '../contexts/HealthDataContext';
import { computeHealthyPercentage } from '../helpers/health';
import { normalizeNumber } from '../helpers/numbers';
import { getUnitIdsByVariant } from '../helpers/units';
import { useNavigateToUnitsAnalysis } from '../hooks/useNavigateToUnitsAnalysis';
import { BigNumberCard } from './components/Card';
import { HealthyDescriptionTooltipContent } from './styles';

interface HealthyProps {
  hasHealth: boolean;
  loading: boolean;
}

export function Healthy({
  hasHealth,
  loading,
}: Readonly<HealthyProps>): ReactElement {
  const history = useHistory();
  const navigateToUnitsAnalysis = useNavigateToUnitsAnalysis();
  const { t } = useTranslation();
  const {
    data,
    loading: loadingHealth,
    offlineRuleActive,
    units,
  } = useHealthDataContext();
  const [profile] = useState(getUserProfile());

  const generalLoading = useMemo(
    () => loading || (loadingHealth && hasHealth),
    [hasHealth, loading, loadingHealth],
  );

  const healthInfo = useMemo(() => {
    if (!data) return undefined;

    return {
      percentageHealthy: computeHealthyPercentage(data, offlineRuleActive),
      healthyUnits: data.healthy,
    };
  }, [data, offlineRuleActive]);

  const hasPredictiveAccess = useMemo(() => {
    const ALLOWED_PREDICTIVE_CLIENT_IDS = new Set([145, 149]);

    return profile.permissions.PER_CLIENT.some(
      (item) => ALLOWED_PREDICTIVE_CLIENT_IDS.has(item.clientId),
    );
  }, [profile.permissions.PER_CLIENT]);

  function indicatorColor(): string {
    if (!healthInfo) return colors.icon['disabled-300'];

    if (healthInfo.percentageHealthy >= 85) return colors.state.success.base;
    if (healthInfo.percentageHealthy >= 45) return colors.state.warning.base;
    return colors.state.danger.base;
  }

  function onClickHealthDetails(): void {
    if (hasPredictiveAccess) {
      history.push('/analise/preditivo');
      return;
    }

    navigateToUnitsAnalysis(getUnitIdsByVariant(units, 'health', 'healthy'));
  }

  const healthyUnitsCount = useMemo(
    () => units.filter((unit) => unit.health === 'healthy').length,
    [units],
  );

  const canSeeDetails = hasPredictiveAccess || healthyUnitsCount > 0;

  return (
    <BigNumberCard
      id="healthy"
      icon="ActivityIcon"
      number={`${normalizeNumber(healthInfo?.percentageHealthy)}%`}
      description={{
        text: t('unidadesSaudaveis'),
        tooltip: <HealthyTooltip />,
      }}
      subDescription={{
        number: normalizeNumber(healthInfo?.healthyUnits),
        text: t('unidadesSemAlerta').toLowerCase(),
      }}
      loading={generalLoading}
      disabledTooltip={generalLoading}
      dataTestId="big-number-card-healthy"
      indicatorColor={indicatorColor()}
      needUpgrade={hasHealth ? undefined : 'health'}
      onClickDetails={hasHealth && canSeeDetails ? onClickHealthDetails : undefined}
      detailsDisabledReasonKey={
        hasHealth && !hasPredictiveAccess && healthyUnitsCount === 0
          ? 'semUnidadesParaVerDetalhes'
          : undefined
      }
    />
  );
}

function HealthyTooltip(): ReactElement {
  const { t } = useTranslation();

  return (
    <HealthyDescriptionTooltipContent>
      <span>{t('unidadesOperacaoComBomDesempenhoDasMaquinas')}</span>
    </HealthyDescriptionTooltipContent>
  );
}
